import { z } from 'zod';
// ---------- Contabilidad por partida doble (núcleo `@jr2/contable`, esquema `ctb`) ----------
//
// Un emisor con régimen PGC lleva diario, mayor y sumas y saldos. Importes en céntimos (enteros);
// fechas AAAA-MM-DD. Los asientos manuales son siempre de tipo NORMAL: la apertura, la
// regularización y el cierre los generará el cierre del ejercicio (T3).
const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha con formato AAAA-MM-DD');
const cents = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
export const CtbActivateSchema = z.object({
    /** Años naturales para los que se abre ejercicio. */
    years: z.array(z.number().int().min(2000).max(2100)).min(1).max(10),
});
export const CtbLineInputSchema = z.object({
    accountCode: z.string().regex(/^[0-9]{1,10}$/, 'Código de cuenta: solo dígitos'),
    debitCents: cents.default(0),
    creditCents: cents.default(0),
    concept: z.string().trim().max(300).nullable().optional(),
});
export const CtbCreateEntrySchema = z.object({
    issuerId: z.string().min(1),
    date: ymd,
    concept: z.string().trim().min(1, 'Indica el concepto').max(300),
    lines: z.array(CtbLineInputSchema).max(200),
    /** Contabilizar al guardar (si no cuadra, no se guarda nada). */
    post: z.boolean().default(false),
});
export const CtbUpdateEntrySchema = z.object({
    issuerId: z.string().min(1),
    date: ymd,
    concept: z.string().trim().min(1).max(300),
    lines: z.array(CtbLineInputSchema).max(200),
    /** Versión leída: control de concurrencia optimista. */
    version: z.number().int().min(1),
});
export const CtbReverseSchema = z.object({
    issuerId: z.string().min(1),
    reason: z.string().trim().min(1, 'Indica el motivo').max(300),
    date: ymd.optional(),
});
export const CtbIssuerQuerySchema = z.object({ issuerId: z.string().min(1) });
export const CtbSubaccountSchema = z.object({
    issuerId: z.string().min(1),
    parentCode: z.string().regex(/^[0-9]{1,10}$/),
    name: z.string().trim().min(1).max(200),
});
export const CtbEntriesQuerySchema = z.object({
    issuerId: z.string().min(1),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    status: z.enum(['DRAFT', 'POSTED', 'VOID']).optional(),
    q: z.string().trim().max(100).optional(),
});
export const CtbLedgerQuerySchema = z.object({
    issuerId: z.string().min(1),
    account: z.string().regex(/^[0-9]{1,10}$/),
    year: z.coerce.number().int().min(2000).max(2100),
    from: ymd.optional(),
    to: ymd.optional(),
});
export const CtbTrialBalanceQuerySchema = z.object({
    issuerId: z.string().min(1),
    year: z.coerce.number().int().min(2000).max(2100),
    /** Longitudes de código a acumular, p. ej. «1,2,3». */
    levels: z
        .string()
        .regex(/^[0-9](,[0-9])*$/)
        .optional(),
    /** Deja fuera los asientos de regularización y cierre (resultado antes de cerrar). */
    excludeClosing: z.enum(['0', '1']).optional(),
});
// ---------- Asientos automáticos (T2) ----------
//
// Qué cuenta del plan usa cada papel en los asientos que genera Toga (facturas, cobros,
// provisiones de fondos). Cada emisor puede cambiarlas; lo que no cambia usa la de por defecto.
export const CTB_ACCOUNT_ROLES = [
    'clients',
    'sales',
    'vat',
    'irpf',
    'advances',
    'supplies',
    'bank',
    'cash',
];
export const CTB_ACCOUNT_DEFAULTS = {
    clients: '430',
    sales: '705',
    vat: '477',
    irpf: '473',
    advances: '438',
    supplies: '555',
    bank: '572',
    cash: '570',
};
export const CTB_ACCOUNT_ROLE_LABEL = {
    clients: {
        label: 'Clientes',
        hint: 'Cada cliente tiene su subcuenta, que se abre sola en la primera factura',
    },
    sales: { label: 'Honorarios (ingresos)', hint: 'Prestaciones de servicios' },
    vat: { label: 'IVA repercutido', hint: 'H.P. IVA repercutido' },
    irpf: { label: 'Retenciones de IRPF', hint: 'H.P. retenciones y pagos a cuenta' },
    advances: {
        label: 'Anticipos de clientes',
        hint: 'Provisiones de fondos recibidas y aún no aplicadas',
    },
    supplies: {
        label: 'Suplidos',
        hint: 'Por defecto 555; pendiente del criterio de la gestoría (¿440?)',
    },
    bank: { label: 'Bancos', hint: 'Cobros por transferencia, tarjeta, domiciliación…' },
    cash: { label: 'Caja', hint: 'Cobros en efectivo' },
};
const accountCode = z.string().regex(/^[0-9]{3,10}$/, 'Código de cuenta: de 3 a 10 dígitos');
export const CtbAccountMapSchema = z
    .object({
    clients: accountCode.optional(),
    sales: accountCode.optional(),
    vat: accountCode.optional(),
    irpf: accountCode.optional(),
    advances: accountCode.optional(),
    supplies: accountCode.optional(),
    bank: accountCode.optional(),
    cash: accountCode.optional(),
})
    .strict();
export const CtbAccountMapUpdateSchema = z.object({
    issuerId: z.string().min(1),
    /** Sustituye a las cuentas elegidas hasta ahora; un papel que falte vuelve a la de por defecto. */
    map: CtbAccountMapSchema,
});
export const CtbBackfillSchema = z.object({ issuerId: z.string().min(1) });
export const CTB_ENTRY_KIND_LABEL = {
    NORMAL: 'Normal',
    OPENING: 'Apertura',
    REGULARIZATION: 'Regularización',
    TAX_EXPENSE: 'Impuesto sobre beneficios',
    EQUITY_TRANSFER: 'Traspaso a patrimonio neto',
    CLOSING: 'Cierre',
    RESULT_APPLICATION: 'Aplicación del resultado',
};
export const CTB_ENTRY_STATUS_LABEL = {
    DRAFT: 'Borrador',
    POSTED: 'Contabilizado',
    VOID: 'Descartado',
};
//# sourceMappingURL=ctb.js.map